import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'antd';
import {
    RollbackOutlined,
    CopyOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    DeleteOutlined,
    DownOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const MessageBubbleActions = ({
    message,
    hasMedia,
    canReplyToThis,
    onAction,
    children,
    isHovered,
    contextMenuOpen,
    setContextMenuOpen,
    isMobile,
    isViewOnly = false,
    canDeleteMessage = false,
    deleteLoading = false
}) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [mobileActionsVisible, setMobileActionsVisible] = useState(false);
    const dropdownOverlayStyle = isMobile
        ? { width: 180, minWidth: 180 }
        : undefined;
    const handleMenuAction = (actionKey) => {
        onAction(actionKey);
        setContextMenuOpen(false);
        if (isMobile) {
            setMobileActionsVisible(false);
        }
    };

    const menuItems = [{
        key: 'reply',
        label: t('reply'),
        icon: <RollbackOutlined />,
        disabled: !canReplyToThis,
        onClick: () => handleMenuAction('reply')
    }, {
        key: 'copy',
        label: t('copy'),
        icon: <CopyOutlined />,
        disabled: !message?.content,
        onClick: () => handleMenuAction('copy')
    }, {
        key: 'download',
        label: t('download'),
        icon: <DownloadOutlined />,
        disabled: !hasMedia,
        onClick: () => handleMenuAction('download')
    }, {
        key: 'details',
        label: t('details'),
        icon: <InfoCircleOutlined />,
        onClick: () => handleMenuAction('details')
    }, {
        key: 'delete',
        label: t('delete'),
        icon: <DeleteOutlined />,
        danger: true,
        disabled: !canDeleteMessage || deleteLoading,
        onClick: () => handleMenuAction('delete')
    }];

    const shouldIgnoreMobileTap = (target) => {
        if (!target?.closest) return false;
        return Boolean(target.closest('a, button, input, textarea, select, video, audio, [role="button"]'));
    };

    const handleMobileTap = (event) => {
        if (!isMobile || shouldIgnoreMobileTap(event.target)) return;
        setMobileActionsVisible(true);
    };

    useEffect(() => {
        if (!isMobile || (!mobileActionsVisible && !contextMenuOpen)) return undefined;

        const handleOutsideClick = (event) => {
            const target = event.target;
            const clickedInsideBubble = containerRef.current?.contains(target);
            const clickedInsideDropdown = target?.closest?.('.ant-dropdown');

            if (!clickedInsideBubble && !clickedInsideDropdown) {
                setContextMenuOpen(false);
                setMobileActionsVisible(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [contextMenuOpen, isMobile, mobileActionsVisible, setContextMenuOpen]);

    if (isViewOnly) {
        return children;
    }

    return (
        <div
            ref={containerRef}
            onClick={handleMobileTap}
            style={{ position: 'relative' }}
        >
            <Dropdown
                menu={{ items: menuItems }}
                trigger={isMobile ? [] : ['contextMenu']}
                overlayStyle={dropdownOverlayStyle}
            >
                {children}
            </Dropdown>

            {(isHovered || contextMenuOpen || (isMobile && mobileActionsVisible)) && (
                <div style={{ position: 'absolute', top: 5, right: 5, zIndex: 10 }}>
                    <Dropdown
                        menu={{ items: menuItems }}
                        trigger={isMobile ? [] : ['click']}
                        open={contextMenuOpen}
                        overlayStyle={dropdownOverlayStyle}
                        onOpenChange={(open) => {
                            setContextMenuOpen(open);
                            if (!open && isMobile) {
                                setMobileActionsVisible(false);
                            }
                        }}
                        placement="bottomRight"
                    >
                        <button
                            type="button"
                            aria-label={t('more_actions')}
                            onClick={(event) => {
                                if (!isMobile) return;
                                event.stopPropagation();
                                setContextMenuOpen(true);
                            }}
                            style={{
                                cursor: 'pointer',
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                border: 'none',
                                padding: 0,
                                background: 'rgba(0, 0, 0, 0.4)',
                                backdropFilter: 'blur(2px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}
                        >
                            <DownOutlined style={{ color: '#fff', fontSize: 10 }} />
                        </button>
                    </Dropdown>
                </div>
            )}
        </div>
    );
};

export default MessageBubbleActions;
